"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";

import {
  TrishOnboardingChat,
  type TrishOnboardingChatProps,
} from "~/components/marketplace/trish-onboarding-chat";
import { getPartnerIntroMessage } from "~/lib/org-onboarding/partner-onboarding-prompt";
import {
  getOrCreateOnboardingSessionKey,
  trackOnboardingSession,
} from "~/lib/org-onboarding/track-onboarding-session";
import { resolveOnboardingChannelFromQuery } from "~/lib/org-onboarding/onboarding-channels";
import type { PartnerOnboardingAgentConfig } from "~/lib/org-onboarding/types";

const PARTNER_SESSION_KEY = "egpt-partner-onboarding-session-key";

export type PartnerOnboardingChatProps = Omit<
  TrishOnboardingChatProps,
  "consultantPersona"
> & {
  partnerConfig: PartnerOnboardingAgentConfig;
};

export function PartnerOnboardingChat({
  partnerConfig,
  onRequestSignUp,
  onSetupAgent,
  ...rest
}: PartnerOnboardingChatProps) {
  const searchParams = useSearchParams();
  const entryChannel = useMemo(
    () => resolveOnboardingChannelFromQuery(searchParams),
    [searchParams],
  );
  const [sessionKey, setSessionKey] = useState<string | null>(null);
  const sessionKeyRef = useRef<string | null>(null);
  const completedRef = useRef(false);
  const signedUpRef = useRef(false);

  const consultantPersona = useMemo(
    () => ({
      name: partnerConfig.displayName,
      avatarSrc: partnerConfig.avatarUrl,
      productName: partnerConfig.productName,
      introMessage: getPartnerIntroMessage({
        agentName: partnerConfig.displayName,
        productName: partnerConfig.productName,
        welcomeIntro: partnerConfig.welcomeIntro,
      }),
    }),
    [partnerConfig],
  );

  useEffect(() => {
    const key = getOrCreateOnboardingSessionKey(PARTNER_SESSION_KEY);
    sessionKeyRef.current = key;
    setSessionKey(key);
    void trackOnboardingSession({
      brandSlug: partnerConfig.brandSlug,
      sessionKey: key,
      status: "started",
      channel: entryChannel,
    });
  }, [entryChannel, partnerConfig.brandSlug]);

  const handleSetupAgent = useCallback(() => {
    if (!completedRef.current && sessionKeyRef.current) {
      completedRef.current = true;
      void trackOnboardingSession({
        brandSlug: partnerConfig.brandSlug,
        sessionKey: sessionKeyRef.current,
        status: "completed",
        channel: entryChannel,
      });
    }
    onSetupAgent();
  }, [entryChannel, onSetupAgent, partnerConfig.brandSlug]);

  const handleRequestSignUp = useCallback(() => {
    if (!signedUpRef.current && sessionKeyRef.current) {
      signedUpRef.current = true;
      void trackOnboardingSession({
        brandSlug: partnerConfig.brandSlug,
        sessionKey: sessionKeyRef.current,
        status: "signed_up",
        channel: entryChannel,
      });
    }
    onRequestSignUp?.();
  }, [entryChannel, onRequestSignUp, partnerConfig.brandSlug]);

  return (
    <TrishOnboardingChat
      {...rest}
      consultantPersona={consultantPersona}
      onSetupAgent={handleSetupAgent}
      onRequestSignUp={onRequestSignUp ? handleRequestSignUp : undefined}
      onboardingSessionTracking={
        sessionKey
          ? {
              brandSlug: partnerConfig.brandSlug,
              sessionKey,
              channel: entryChannel,
            }
          : undefined
      }
    />
  );
}
